import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AssistantQuickQuestionsResponse } from './chatbot-ia.service';
import { UserService } from '../user.service';

export interface QuickQuestionsCacheEntry {
  version: 5;
  pageKey: string;
  headline: string;
  message: string;
  subtitle: string;
  quickQuestions: string[];
  generated: boolean;
  cachedAt: number;
}

const CACHE_VERSION = 5 as const;
const STORAGE_PREFIX = 'wosi-chat-quick-questions:v5';
const TTL_ANON_MS = 24 * 60 * 60 * 1000;
const TTL_USER_MS = 12 * 60 * 60 * 1000;
const TTL_ADMIN_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class ChatbotQuickQuestionsCacheService {
  private platformId = inject(PLATFORM_ID);
  private userService = inject(UserService);

  private scopeKey = 'anon';

  refreshScope(): void {
    const userId = this.userService.getUser()?.id;
    this.scopeKey = userId ? `user:${userId}` : 'anon';
  }

  load(pageKey: string): QuickQuestionsCacheEntry | null {
    if (!isPlatformBrowser(this.platformId) || !pageKey) return null;

    this.refreshScope();

    try {
      const raw = localStorage.getItem(this.storageKey(pageKey));
      if (!raw) return null;

      const parsed = JSON.parse(raw) as QuickQuestionsCacheEntry;
      if (!this.isValidEntry(parsed, pageKey)) return null;

      return parsed;
    } catch {
      return null;
    }
  }

  save(response: AssistantQuickQuestionsResponse, pageKey: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!response.quickQuestions?.length || !pageKey) return;

    this.refreshScope();

    const entry: QuickQuestionsCacheEntry = {
      version: CACHE_VERSION,
      pageKey,
      headline: response.headline?.trim() || '',
      message: response.message?.trim() || '',
      subtitle: response.subtitle?.trim() || '',
      quickQuestions: response.quickQuestions.slice(0, 4),
      generated: response.generated === true,
      cachedAt: Date.now(),
    };

    try {
      localStorage.setItem(this.storageKey(pageKey), JSON.stringify(entry));
    } catch {
      // quota exceeded — fail silently
    }
  }

  isFresh(entry: QuickQuestionsCacheEntry | null, pageKey: string): boolean {
    if (!entry || entry.pageKey !== pageKey) return false;

    const ttl = this.ttlMs();
    return Date.now() - entry.cachedAt < ttl;
  }

  private ttlMs(): number {
    if (this.scopeKey === 'anon') return TTL_ANON_MS;
    if (this.userService.getUser()?.hierarchy === 'admin') return TTL_ADMIN_MS;
    return TTL_USER_MS;
  }

  private storageKey(pageKey: string): string {
    return `${STORAGE_PREFIX}:${this.scopeKey}:${pageKey}`;
  }

  private isValidEntry(
    value: QuickQuestionsCacheEntry | null | undefined,
    pageKey: string
  ): value is QuickQuestionsCacheEntry {
    if (!value || value.version !== CACHE_VERSION) return false;
    if (value.pageKey !== pageKey) return false;
    if (!Array.isArray(value.quickQuestions) || value.quickQuestions.length < 3) {
      return false;
    }
    if (typeof value.cachedAt !== 'number') return false;

    return (
      typeof value.headline === 'string' &&
      typeof value.message === 'string' &&
      typeof value.subtitle === 'string'
    );
  }
}
